import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle, Truck, Wallet, IndianRupee, Calendar,
  FileDown, Calculator, RefreshCw, ArrowUpRight,
} from "lucide-react";
import { Link } from "react-router-dom";

const fmt = (n) => (
  n == null ? "—" : "₹" + Math.round(Number(n)).toLocaleString("en-IN")
);

function Tile({ label, value, sub, tone = "slate", icon: Icon }) {
  const toneClass = {
    red: "bg-red-50 border-red-200 text-red-800",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    slate: "bg-white border-slate-200 text-slate-700",
  }[tone];
  return (
    <div className={`rounded-lg border p-4 ${toneClass}`}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
        {Icon && <Icon className="h-4 w-4 opacity-60" />}
      </div>
      <div className="text-3xl font-bold mt-2 font-mono">{value}</div>
      {sub && <div className="text-[11px] mt-1 opacity-70">{sub}</div>}
    </div>
  );
}

export default function AccountsDashboard() {
  const [dash, setDash] = useState({});
  const [fastag, setFastag] = useState({ rows: [], threshold: 0 });
  const [lm, setLm] = useState({ balance: 0, threshold: 0, is_low: false, ledger: [] });
  const [trips, setTrips] = useState([]);
  const [imprestSummary, setImprestSummary] = useState({ rows: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const [d, f, l, t, s] = await Promise.all([
        api.get("/logistics/dashboard"),
        api.get("/logistics/fastag/latest"),
        api.get("/logistics/lm-imprest"),
        api.get("/logistics/trips", { params: { status: "open" } }),
        api.get("/logistics/imprest/summary"),
      ]);
      setDash(d.data);
      setFastag(f.data);
      setLm(l.data);
      setTrips(t.data);
      setImprestSummary(s.data);
    } catch (e) {
      // tolerate partial failures — keep UI alive
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return <div className="p-8 text-sm text-slate-500">Loading Accounts console…</div>;
  }

  const lowFastag = fastag.rows.filter(r => r.is_low);
  const pendingFastag = fastag.rows.filter(r => r.pending_today);
  // payment-pending trips sorted by pending desc
  const pendingTrips = [...trips]
    .filter(t => (Number(t.pending) || 0) > 0)
    .sort((a, b) => (Number(b.pending) || 0) - (Number(a.pending) || 0))
    .slice(0, 15);

  return (
    <div data-testid="accounts-dashboard" className="p-6 space-y-6 max-w-7xl">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Console</div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Accounts Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Read-only view of logistics financials — FASTag, LM imprest pool, payment-pending trips & driver imprest.
          </p>
        </div>
        <Button onClick={load} variant="outline" size="sm" disabled={refreshing}
                data-testid="accounts-refresh-btn">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Top tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Tile label="LM Imprest balance" tone={lm.is_low ? "red" : "emerald"}
              value={fmt(lm.balance)}
              sub={lm.is_low ? `LOW — below ₹${lm.threshold?.toLocaleString("en-IN")}` : "healthy"}
              icon={Wallet} />
        <Tile label="Low FASTag trucks" tone={lowFastag.length ? "red" : "slate"}
              value={lowFastag.length}
              sub={`Threshold ${fmt(fastag.threshold)}`}
              icon={AlertTriangle} />
        <Tile label="FASTag pending today" tone={pendingFastag.length ? "amber" : "slate"}
              value={fastag.non_working_day ? "—" : pendingFastag.length}
              sub={fastag.non_working_day ? "Non-working day" : "Awaiting LM update"}
              icon={Calendar} />
        <Tile label="Open trips" value={dash.open_trips ?? 0}
              tone={(dash.open_trips ?? 0) > 0 ? "blue" : "slate"}
              sub={`${pendingTrips.length} with pending balance`}
              icon={Truck} />
        <Tile label="Driver-wise pending" tone={(dash.total_driver_pending || 0) > 0 ? "red" : "slate"}
              value={fmt(dash.total_driver_pending)}
              sub={`Across ${imprestSummary.rows.length} driver(s)`}
              icon={IndianRupee} />
      </div>

      {/* Low FASTag list */}
      <Section title="Low FASTag trucks" subtitle="Need recharge"
                 link="/logistics/hub" linkLabel="Open Logistics Hub →">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Truck</th>
              <th className="text-left px-3 py-2">Last Updated</th>
              <th className="text-right px-3 py-2">Latest Balance</th>
              <th className="text-right px-3 py-2">Threshold</th>
              <th className="text-right px-3 py-2">Deficit</th>
            </tr>
          </thead>
          <tbody>
            {lowFastag.length === 0 && (
              <tr><td colSpan={5} className="text-center py-6 text-xs text-emerald-700">
                All trucks above threshold ✓
              </td></tr>
            )}
            {lowFastag.map(r => (
              <tr key={r.id} className="border-t border-slate-100"
                  data-testid={`acc-low-fastag-${r.truck_no}`}>
                <td className="px-3 py-2 font-mono">{r.truck_no}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{r.latest_date || "—"}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold text-red-700">
                  {fmt(r.latest_balance)}
                </td>
                <td className="px-3 py-2 text-right text-xs font-mono">{fmt(r.threshold)}</td>
                <td className="px-3 py-2 text-right font-mono text-red-700">
                  {r.latest_balance != null ? "-" + fmt(r.threshold - r.latest_balance) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* Payment-pending trips */}
      <Section title="Payment-pending trips" subtitle="Top 15 by pending amount"
                 link="/logistics/hub" linkLabel="View all trips →">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Truck</th>
              <th className="text-left px-3 py-2">Driver</th>
              <th className="text-left px-3 py-2">Location</th>
              <th className="text-right px-3 py-2">To Pay</th>
              <th className="text-right px-3 py-2">Paid</th>
              <th className="text-right px-3 py-2">Pending</th>
            </tr>
          </thead>
          <tbody>
            {pendingTrips.length === 0 && (
              <tr><td colSpan={7} className="text-center py-6 text-xs text-emerald-700">
                No payment-pending trips ✓
              </td></tr>
            )}
            {pendingTrips.map(t => (
              <tr key={t.id} className="border-t border-slate-100"
                  data-testid={`acc-pending-trip-${t.id}`}>
                <td className="px-3 py-2 text-xs">{t.date}</td>
                <td className="px-3 py-2 font-mono text-xs">{t.truck_no}</td>
                <td className="px-3 py-2 text-xs">{t.driver_name || "—"}</td>
                <td className="px-3 py-2 text-xs">{t.location_name || "—"}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{fmt(t.amount_to_pay)}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{fmt(t.amount_paid)}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold text-red-700">
                  {fmt(t.pending)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* Driver-wise imprest summary */}
      <Section title="Driver imprest summary" subtitle="Per-driver outstanding"
                 link="/logistics/hub" linkLabel="Driver imprest →">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Driver</th>
              <th className="text-left px-3 py-2">Code</th>
              <th className="text-right px-3 py-2">Topups</th>
              <th className="text-right px-3 py-2">Pending</th>
              <th className="text-right px-3 py-2">Outstanding</th>
            </tr>
          </thead>
          <tbody>
            {imprestSummary.rows.length === 0 && (
              <tr><td colSpan={5} className="text-center py-6 text-xs text-slate-500">
                No drivers with activity yet.
              </td></tr>
            )}
            {imprestSummary.rows.slice(0, 20).map(r => (
              <tr key={r.driver_id} className="border-t border-slate-100"
                  data-testid={`acc-imprest-${r.driver_code || r.driver_id}`}>
                <td className="px-3 py-2 text-xs font-medium">{r.driver_name}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{r.driver_code || "—"}</td>
                <td className="px-3 py-2 text-right text-xs font-mono">{fmt(r.balance)}</td>
                <td className="px-3 py-2 text-right text-xs font-mono">{fmt(r.pending)}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold text-red-700">
                  {fmt(r.outstanding)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* LM Imprest ledger preview */}
      <Section title="LM Imprest — recent activity" subtitle="Last 10 entries">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-right px-3 py-2">Amount</th>
              <th className="text-right px-3 py-2">Running</th>
              <th className="text-left px-3 py-2">Note</th>
            </tr>
          </thead>
          <tbody>
            {lm.ledger.slice(0, 10).map(r => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-3 py-2 text-xs">{r.date || r.created_at?.slice(0, 10)}</td>
                <td className="px-3 py-2 text-xs">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                    r.entry_type === "topup" ? "bg-emerald-100 text-emerald-800"
                      : r.entry_type === "trip_settle" ? "bg-blue-100 text-blue-800"
                      : "bg-slate-100 text-slate-700"
                  }`}>{r.entry_type.replace("_", " ")}</span>
                </td>
                <td className={`px-3 py-2 text-right font-mono ${r.amount >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                  {r.amount >= 0 ? "+" : ""}{fmt(r.amount)}
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs">{fmt(r.running_balance)}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{r.note || "—"}</td>
              </tr>
            ))}
            {lm.ledger.length === 0 && (
              <tr><td colSpan={5} className="text-center py-6 text-xs text-slate-500">
                No LM imprest activity yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </Section>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
        <QuickAction to="/admin/advances" icon={IndianRupee} label="Advance approvals"
                       hint="Approve/decline employee advances" />
        <QuickAction to="/admin/advances/reports" icon={FileDown} label="Advance reports"
                       hint="Export ledger & reconciliation" />
        <QuickAction to="/admin/salary-calculator" icon={Calculator} label="Salary calculator"
                       hint="CTC / take-home estimator" />
        <QuickAction to="/logistics/hub" icon={Truck} label="Logistics Hub"
                       hint="Full trips / imprest detail" />
      </div>
    </div>
  );
}

function Section({ title, subtitle, link, linkLabel, children }) {
  return (
    <div className="space-y-2">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-base font-semibold text-slate-900" style={{ fontFamily: "Chivo" }}>
            {title}
          </h2>
          {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
        </div>
        {link && (
          <Link to={link} className="text-xs brand-text hover:underline">
            {linkLabel || "View →"}
          </Link>
        )}
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {children}
      </div>
    </div>
  );
}

function QuickAction({ to, icon: Icon, label, hint }) {
  return (
    <Link to={to} data-testid={`acc-quick-${to.replace(/\//g, "-")}`}
          className="group bg-white border border-slate-200 rounded-lg p-4 hover:border-red-300 hover:bg-red-50/30 transition">
      <div className="flex items-start justify-between">
        <Icon className="h-5 w-5 text-slate-600 group-hover:brand-text" />
        <ArrowUpRight className="h-3.5 w-3.5 text-slate-400 group-hover:brand-text" />
      </div>
      <div className="text-sm font-semibold text-slate-900 mt-3">{label}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>
    </Link>
  );
}
